import React from 'react';
import { useAtom } from 'jotai';
import { v4 as uuid } from 'uuid';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import useTranslation from 'next-translate/useTranslation';
import { Stack } from '@spaceshipapp/cambridge';
import useYup from '../../hooks/useYup';
import Button from '../../components/Button';
import { typeAtom, parcelsAtom, parcelMapAtom } from '../../contexts/shipment';
import Parcel from './Parcel';
import { Envelope } from './Document';
import { ShipmentType } from '../../types/common';
import { getPresetSize } from './utils';

const preset = getPresetSize('small', ShipmentType.PARCEL);

interface Props {
  errors?: ReturnType<typeof useYup>['errors'];
}

const Parcels: React.FC<Props> = ({ errors }) => {
  const { t } = useTranslation('common');
  const [shipmentType] = useAtom(typeAtom);
  const [parcels, setParcels] = useAtom(parcelsAtom);
  const [, setParcelMap] = useAtom(parcelMapAtom);
  const allowRemove = parcels.length > 1;

  const handleAddParcel = () => {
    const parcelId = uuid();
    setParcelMap((prev) => ({ ...prev, [parcelId]: preset }));
    setParcels((prev) => [...prev, parcelId]);
  };

  const handleRemoveParcel = (parcelId: string) => {
    setParcels((prev) => prev.filter((id) => id !== parcelId));
  };

  return shipmentType === ShipmentType.PARCEL ? (
    <Stack>
      {parcels.map((parcelId, index) => (
        <Parcel
          key={parcelId}
          parcelId={parcelId}
          allowRemove={allowRemove}
          error={errors?.[`parcelItems[${index}]`]}
          onRemove={() => handleRemoveParcel(parcelId)}
        />
      ))}
      <Placeholder>
        <Button
          size="sm"
          className={btnStyle}
          iconName="Plus"
          onClick={handleAddParcel}
          aria-label={t('Parcel.btnAdd')}
          data-tracking-label={t('commonEn:Parcel.btnAdd')}
        />
      </Placeholder>
    </Stack>
  ) : (
    <Envelope errors={errors} />
  );
};

const Placeholder = styled.div`
  display: flex;
  justify-content: center;
`;

const btnStyle = css`
  border-radius: 100%;

  &:before {
    border-radius: 100%;
  }
`;

export default Parcels;
